import { useEffect, useRef, useState } from 'react';
import {
  fetchStripeSubscriptionStatus,
  createStripeCheckout,
  cancelStripeSubscription,
  createStripePortalSession,
  createPaypalCheckout,
  cancelPaypalSubscription,
  createPixCheckout,
  fetchPixPaymentStatus,
  fetchPlans,
  isSelfServicePlan,
  type PlanInfo,
  type StripeSubscriptionStatus,
  type PixCheckoutResponse,
} from '../services/api';
import { useT } from '../i18n';

interface SubscriptionProps {
  onNavigate?: (view: 'billing' | 'account') => void;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Ativa',
  pending_payment: 'Aguardando pagamento',
  cancelled_at_period_end: 'Cancelada no fim do periodo',
  cancelled: 'Cancelada',
  suspended: 'Suspensa',
};

// Issue #593: rotulos dos planos do contrato #592 (free/single/team/
// enterprise). 'pro' mantido para exibir assinaturas legadas ativas.
const PLAN_LABEL: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  single: 'Single',
  team: 'Team',
  enterprise: 'Enterprise',
};

// Opcoes de checkout self-service (Stripe/PayPal/PIX) — DERIVADAS da API
// /api/billing/plans (review F2 do PR #595) via isSelfServicePlan() em
// services/api.ts. Assim a pagina funciona tanto na janela em que o
// backend ainda serve 'pro' (contrato legado) quanto apos o #592
// ('single'/'team'). Enterprise/contact-only nunca entra — mailto
// vendas@auranota.app na pagina de Planos.

// Reativacao: prefere o proprio plano da assinatura se a API ainda o
// oferece (backend legado devolve 'pro' -> manda 'pro'). Se nao existe
// mais, 'pro' cai para 'single' (mesmo tier R$99 do contrato #592); por
// fim, o primeiro plano self-service disponivel.
function toCheckoutPlan(plan: string | undefined, available: PlanInfo[]): string {
  const keys = available.map((p) => p.plan);
  if (plan && keys.includes(plan)) return plan;
  if (plan === 'pro' && keys.includes('single')) return 'single';
  return keys[0] ?? '';
}

const METHOD_LABEL: Record<string, string> = {
  stripe: 'Cartao (Stripe)',
  paypal: 'PayPal',
  pix: 'PIX',
};

export default function Subscription({ onNavigate }: SubscriptionProps) {
  const t = useT();
  const [sub, setSub] = useState<StripeSubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  // PIX state
  const [pixCharge, setPixCharge] = useState<PixCheckoutResponse | null>(null);
  const [pixPolling, setPixPolling] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);
  const pollRef = useRef<number | null>(null);
  // Issue #593 + review F2: opcoes/alvo do checkout vem da API —
  // planos pagos self-service (single/team no contrato #592; 'pro'
  // enquanto o backend legado nao e migrado).
  const [checkoutPlans, setCheckoutPlans] = useState<PlanInfo[]>([]);
  const [checkoutPlan, setCheckoutPlan] = useState('');

  function loadStatus() {
    setLoading(true);
    fetchStripeSubscriptionStatus()
      .then((s) => setSub(s))
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar assinatura.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadStatus();
    // Opcoes de checkout derivadas do que a API oferece de fato (F2).
    fetchPlans()
      .then((resp) => {
        const opts = resp.plans.filter(isSelfServicePlan);
        setCheckoutPlans(opts);
        setCheckoutPlan((prev) =>
          prev && opts.some((p) => p.plan === prev)
            ? prev
            : (opts[0]?.plan ?? ''),
        );
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Erro ao carregar planos.'),
      );
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, []);

  // Detecta retorno do checkout via query param (Stripe/PayPal).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const statusParam = params.get('status');
    if (statusParam === 'success') {
      setInfo('Pagamento concluido! Sua assinatura esta sendo ativada.');
      loadStatus();
    } else if (statusParam === 'cancelled') {
      setInfo('Checkout cancelado. Voce pode tentar novamente quando quiser.');
    }
  }, []);

  async function handleStripeUpgrade(plan: string) {
    setActionLoading(true);
    setError(null);
    setInfo(null);
    try {
      const res = await createStripeCheckout(plan);
      window.location.href = res.checkout_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao iniciar checkout.');
      setActionLoading(false);
    }
  }

  async function handlePaypalUpgrade(plan: string) {
    setActionLoading(true);
    setError(null);
    setInfo(null);
    try {
      const res = await createPaypalCheckout(plan);
      if (res.approval_url) {
        window.location.href = res.approval_url;
      } else {
        setError('PayPal nao retornou URL de aprovacao.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao iniciar checkout PayPal.');
      setActionLoading(false);
    }
  }

  async function handlePixCheckout(plan: string) {
    setActionLoading(true);
    setError(null);
    setInfo(null);
    setPixCopied(false);
    try {
      const res = await createPixCheckout(plan);
      setPixCharge(res);
      setInfo('Escaneie o QR Code ou copie o codigo PIX para pagar.');
      startPixPolling(res.txid);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao gerar PIX.');
    } finally {
      setActionLoading(false);
    }
  }

  function startPixPolling(txid: string) {
    if (pollRef.current) window.clearInterval(pollRef.current);
    setPixPolling(true);
    pollRef.current = window.setInterval(async () => {
      try {
        const st = await fetchPixPaymentStatus(txid);
        if (st.paid) {
          if (pollRef.current) window.clearInterval(pollRef.current);
          setPixPolling(false);
          setPixCharge(null);
          setInfo('Pagamento PIX confirmado! Assinatura ativada.');
          loadStatus();
        }
      } catch {
        // ignora erros transientes de polling
      }
    }, 5000);
  }

  function copyPixCode() {
    if (!pixCharge) return;
    navigator.clipboard.writeText(pixCharge.br_code).then(() => {
      setPixCopied(true);
      setTimeout(() => setPixCopied(false), 2000);
    });
  }

  async function handleCancel() {
    if (!confirm('Cancelar a assinatura? O acesso e mantido ate o fim do periodo pago.')) return;
    setActionLoading(true);
    setError(null);
    setInfo(null);
    try {
      const method = sub?.payment_method;
      const res = method === 'paypal'
        ? await cancelPaypalSubscription()
        : await cancelStripeSubscription();
      setSub(res.subscription);
      setInfo(res.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao cancelar assinatura.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handlePortal() {
    setActionLoading(true);
    setError(null);
    try {
      const res = await createStripePortalSession();
      window.location.href = res.portal_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao abrir portal.');
    } finally {
      setActionLoading(false);
    }
  }

  const isActive = sub?.status === 'active';
  const isCancelledAtEnd = sub?.status === 'cancelled_at_period_end';

  return (
    <section className="rounded-lg border border-brand-200 bg-white p-8 shadow-sm">
      <h1 className="mb-4 text-2xl font-bold text-brand-900">Assinatura</h1>

      {info && (
        <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          {info}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-brand-600">Carregando...</p>}

      {!loading && sub && (
        <div className="mb-6 space-y-2">
          <div className="rounded-md border border-brand-100 bg-brand-50 p-4">
            <p className="text-sm text-brand-600">
              <span className="font-medium text-brand-800">Plano atual: </span>
              {PLAN_LABEL[sub.plan] ?? sub.plan}
            </p>
            <p className="text-sm text-brand-600">
              <span className="font-medium text-brand-800">Status: </span>
              {STATUS_LABEL[sub.status] ?? sub.status}
            </p>
            {sub.current_period_end && (
              <p className="text-sm text-brand-600">
                <span className="font-medium text-brand-800">Renovacao em: </span>
                {formatDate(sub.current_period_end)}
              </p>
            )}
            {sub.payment_method && (
              <p className="text-sm text-brand-600">
                <span className="font-medium text-brand-800">Metodo: </span>
                {METHOD_LABEL[sub.payment_method] ?? sub.payment_method}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Bloco PIX: QR Code + Copia e Cola */}
      {pixCharge && (
        <div className="mb-6 rounded-md border border-brand-200 bg-brand-50 p-4">
          <h2 className="mb-2 text-base font-semibold text-brand-900">Pague com PIX</h2>
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
            {pixCharge.qr_code_base64 && (
              <img
                src={`data:image/png;base64,${pixCharge.qr_code_base64}`}
                alt="QR Code PIX"
                className="h-48 w-48 rounded border border-brand-200 bg-white p-2"
              />
            )}
            <div className="flex-1">
              <p className="mb-2 text-sm text-brand-700">
                Escaneie o QR Code ou copie o codigo abaixo para o app do seu banco.
              </p>
              <textarea
                readOnly
                value={pixCharge.br_code}
                className="w-full rounded border border-brand-200 bg-white p-2 text-xs text-brand-800"
                rows={4}
              />
              <button
                onClick={copyPixCode}
                className="mt-2 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
              >
                {pixCopied ? 'Copiado!' : 'Copiar codigo PIX'}
              </button>
              <p className="mt-2 text-xs text-brand-500">
                {pixPolling ? 'Aguardando confirmacao de pagamento...' : 'Validade: 30 minutos.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {!loading && (
        <div className="flex flex-wrap gap-3">
          {!isActive && !isCancelledAtEnd && checkoutPlans.length > 0 && (
            <div className="w-full space-y-3">
              {/* Issue #593/F2: seletor com os planos self-service que a
                  API oferece (nomes de p.name — fonte unica). */}
              <div className="flex flex-wrap gap-2" role="group" aria-label="Plano">
                {checkoutPlans.map((p) => (
                  <button
                    key={p.plan}
                    type="button"
                    onClick={() => setCheckoutPlan(p.plan)}
                    aria-pressed={checkoutPlan === p.plan}
                    className={`rounded-md border px-4 py-2 text-sm font-medium ${
                      checkoutPlan === p.plan
                        ? 'border-brand-600 bg-brand-50 text-brand-800'
                        : 'border-brand-300 bg-white text-brand-600 hover:bg-brand-50'
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleStripeUpgrade(checkoutPlan)}
                  disabled={actionLoading || !checkoutPlan}
                  className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  Assinar {checkoutPlans.find((p) => p.plan === checkoutPlan)?.name ?? checkoutPlan} (Stripe)
                </button>
                <button
                  onClick={() => handlePaypalUpgrade(checkoutPlan)}
                  disabled={actionLoading || !checkoutPlan}
                  className="rounded-md bg-[#0070ba] px-4 py-2 text-sm font-medium text-white hover:bg-[#005ea6] disabled:opacity-50"
                >
                  Assinar {checkoutPlans.find((p) => p.plan === checkoutPlan)?.name ?? checkoutPlan} (PayPal)
                </button>
                <button
                  onClick={() => handlePixCheckout(checkoutPlan)}
                  disabled={actionLoading || !checkoutPlan}
                  className="rounded-md bg-[#32bcad] px-4 py-2 text-sm font-medium text-white hover:bg-[#29a89a] disabled:opacity-50"
                >
                  Pagar {checkoutPlans.find((p) => p.plan === checkoutPlan)?.name ?? checkoutPlan} via PIX
                </button>
              </div>
            </div>
          )}

          {isActive && (
            <button
              onClick={handleCancel}
              disabled={actionLoading}
              className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Cancelar no fim do periodo
            </button>
          )}

          {isCancelledAtEnd && checkoutPlans.length > 0 && (
            <button
              onClick={() => handleStripeUpgrade(toCheckoutPlan(sub?.plan, checkoutPlans))}
              disabled={actionLoading}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Reativar assinatura
            </button>
          )}

          {sub?.payment_method === 'stripe' && (
            <button
              onClick={handlePortal}
              disabled={actionLoading}
              className="rounded-md border border-brand-300 bg-white px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
            >
              Gerenciar no Stripe Portal
            </button>
          )}
        </div>
      )}

      <div className="mt-6 text-sm text-brand-500">
        <p>
          Pagamento recorrente automatico via Stripe ou PayPal. O cancelamento
          entra em vigor no fim do periodo ja pago — sem cobranca imediata. PIX
          gera um BR Code real (BACEN) e ativa a assinatura apos confirmacao.
        </p>
      </div>
    </section>
  );
}
